import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth-compat";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useRole } from "@/contexts/RoleContext";
import { switchDefaultRole, setDefaultRole, type Role } from "@/services/UserManagmentServices";
import { ROLE_ONBOARDING_ROUTE } from "@/lib/roleDisplay";
import { toast } from "sonner";
import {
  GraduationCap, BookOpen, ArrowRight, Check,
  Users, Search, MessageCircle, Star, ArrowLeftRight,
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { cn } from "@/lib/utils";
import { usePageTitle } from "@/hooks/usePageTitle";

const roleConfig = {
  TEACHER: {
    icon: GraduationCap,
    label: "Teacher",
    tagline: "Teach, earn, and grow your teaching business.",
    headerGradient: "from-indigo-700 to-indigo-800",
    accentText: "text-emerald-700",
    accentLight: "bg-emerald-50 border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    ctaGradient: "from-indigo-700 to-indigo-800",
    features: [
      { icon: Star,          text: "Set up your public teaching profile" },
      { icon: BookOpen,      text: "Create and manage your offerings" },
      { icon: MessageCircle, text: "Respond to student inquiries" },
      { icon: Users,         text: "Build your student community" },
    ],
    destination: "/teacher-dashboard",
  },
  STUDENT: {
    icon: BookOpen,
    label: "Student",
    tagline: "Find teachers, learn new skills, and reach your goals.",
    headerGradient: "from-violet-700 to-indigo-700",
    accentText: "text-violet-700",
    accentLight: "bg-violet-50 border-violet-200",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
    ctaGradient: "from-violet-700 to-indigo-700",
    features: [
      { icon: Search,        text: "Discover verified teachers" },
      { icon: MessageCircle, text: "Send inquiries and book sessions" },
      { icon: Star,          text: "Track your learning progress" },
      { icon: Users,         text: "Access group classes and courses" },
    ],
    destination: "/student-dashboard",
  },
};

export default function RoleSwitchPage() {
  usePageTitle('Switching Role | BookMyCoaching');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { refetch } = useCurrentUser();
  const [switching, setSwitching] = useState(false);
  const [switched, setSwitched] = useState(false);

  const targetRole = (searchParams.get("to") as Role) ?? "STUDENT";
  const acquire = searchParams.get("acquire") === "true";
  const fromRole: Role = targetRole === "TEACHER" ? "STUDENT" : "TEACHER";
  const config = roleConfig[targetRole] ?? roleConfig.STUDENT;
  const fromConfig = roleConfig[fromRole] ?? roleConfig.TEACHER;
  const Icon = config.icon;
  const FromIcon = fromConfig.icon;

  const { setRole } = useRole();
  const hasToken = !!(auth.user?.access_token || sessionStorage.getItem("access_token"));

  useEffect(() => {
    if (!hasToken) navigate("/login");
  }, [hasToken]);

  async function handleSwitch() {
    setSwitching(true);
    try {
      const token = await auth.getToken();
      if (acquire) {
        // Role doesn't exist yet — create it then send the user to onboarding.
        await setDefaultRole(token, targetRole);
        await switchDefaultRole(token, targetRole);
        await refetch();
        setRole(targetRole.toLowerCase());
        setSwitched(true);
        // replace: true so this transition screen doesn't sit in browser
        // history between the page the user switched from and where they
        // land — otherwise one "back" press lands back on this transient
        // confirmation screen instead of the actual previous page.
        setTimeout(() => navigate(ROLE_ONBOARDING_ROUTE[targetRole], { replace: true }), 900);
      } else {
        await switchDefaultRole(token, targetRole);
        await refetch();
        setRole(targetRole.toLowerCase());
        setSwitched(true);
        // Same reasoning as above.
        setTimeout(() => navigate(config.destination, { replace: true }), 900);
      }
    } catch {
      setSwitching(false);
      toast.error("Failed to switch role. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center px-4 py-10 pt-28">
        <div className="w-full max-w-lg space-y-5">

          {/* Role transition indicator */}
          <div className="flex items-center justify-center gap-4">
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-12 h-12 rounded-xl border border-slate-200 bg-white shadow-sm flex items-center justify-center opacity-50">
                <FromIcon className="w-5 h-5 text-slate-500" />
              </div>
              <span className="text-xs text-slate-400 font-medium">{fromConfig.label}</span>
            </div>

            <div className="w-8 h-8 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center">
              <ArrowLeftRight className="w-3.5 h-3.5 text-slate-400" />
            </div>

            <div className="flex flex-col items-center gap-1.5">
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br shadow-md",
                config.ctaGradient,
              )}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-bold text-slate-700">{config.label}</span>
            </div>
          </div>

          {/* Main card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

            {/* Header band */}
            <div className={cn("bg-gradient-to-br px-6 py-6", config.headerGradient)}>
              <h1 className="text-xl font-bold text-white leading-tight">
                Switch to {config.label}
              </h1>
              <p className="text-sm text-white/70 mt-1 leading-relaxed">{config.tagline}</p>
            </div>

            <div className="divide-y divide-slate-100">

              {/* What changes */}
              <div className="px-6 py-5">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">
                  What changes
                </p>
                <ul className="space-y-3">
                  {config.features.map(({ icon: FIcon, text }, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                        config.iconBg,
                      )}>
                        <FIcon className={cn("w-4 h-4", config.iconColor)} />
                      </div>
                      <span className="text-sm text-slate-700">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What stays the same */}
              <div className="px-6 py-5">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                  What stays the same
                </p>
                <div className="space-y-2.5">
                  {[
                    "Your account and login credentials",
                    `Your ${fromConfig.label} profile and data are preserved`,
                    "You can switch back at any time from the sidebar",
                  ].map((line, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                      {line}
                    </div>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="px-6 py-5 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate(-1)}
                  disabled={switching || switched}
                  className="sm:flex-none px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-all duration-200 disabled:opacity-40"
                >
                  Stay as {fromConfig.label}
                </button>
                <button
                  onClick={handleSwitch}
                  disabled={switching || switched}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold transition-all duration-300 shadow-sm",
                    switched
                      ? "bg-emerald-600 text-white"
                      : cn("bg-gradient-to-br text-white hover:opacity-90 hover:shadow-md active:scale-[0.98]", config.ctaGradient),
                    (switching || switched) && "opacity-90"
                  )}
                >
                  {switched ? (
                    <><Check className="w-4 h-4" /> Switched — heading over…</>
                  ) : switching ? (
                    <><span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Switching…</>
                  ) : (
                    <>Switch to {config.label} <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>

            </div>
          </div>

          <p className="text-center text-xs text-slate-400">
            You can always switch back from the sidebar menu.
          </p>

        </div>
      </div>
    </div>
  );
}
